/* eslint-disable max-len */
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import Summary from './Summary'
import withPlayerApi from '../../hocs/withPlayerApi'
import getImageRenditions from '../../utils/getImageRenditions'
import setPageName from '../../utils/pageName'
import handleApiError from '../../utils/handleApiError'
import cookies from '../../utils/cookies'
import extractNewsData from '../../utils/extractNewsData'
import {
  getPuffs,
  getFeaturedEpisode,
  getFeaturedShortform,
  getTabs,
} from './summaryData'
import {
  DEFAULT_GROUPTOKEN, STV_COOKIES, EU_REGION_CODE, POSTER_RENDITIONS,
} from '../../constants'
import getCurrentDomain from '../../utils/getCurrentDomain'
import { setHideSponsor } from '../../actions'

const SummaryContainer = (props) => {
  useEffect(() => {
    setPageName({
      page: 'programme',
      guid: props?.data?.programmeGuid,
    })
  }, [])
  return <Summary {...props} />
}

SummaryContainer.propTypes = {
  data: PropTypes.shape({
    programmeGuid: PropTypes.string,
  }).isRequired,
}

const extractProgrammeHeaderData = programmeData => ({
  name: programmeData.name,
  availableEpisodes: programmeData.availableEpisodes,
  longDescription: programmeData.longDescription,
  image: programmeData?.images?.length > 0 && programmeData?.images?.[0]?._filepath && getImageRenditions(
    programmeData?.images?.[0]?._filepath,
    ['720x405', '480x270'],
  ),
  svodExclusive: programmeData?.svodExclusive,
  svodProhibited: programmeData?.svodProhibited,
  forceAds: programmeData?.forceAds,
  autoplayShortform: programmeData?.autoplayShortform,
})

const extractRecommendedData = r => ({
  programmeName: r.name,
  numEpisodes: parseInt(r.availableEpisodes, 10),
  link: `/summary/${r.id}`,
  image: getImageRenditions(
    r?.images?.find(poster => poster.imageType === 'PPMB')?._filepath,
    POSTER_RENDITIONS
  ),
  id: r.id,
  genre: r?.genre?.name,
})

export function extractNextEpisodeData(programmeData) {
  return {
    title: programmeData?.scheduleNext?.title,
    startTime: programmeData?.scheduleNext?.startTime,
  }
}

SummaryContainer.getInitialProps = async ({
  query,
  playerApi,
  req,
  res,
  reduxStore,
}) => {
  // TODO: Extract and refactor this logic, external file.
  // This list of guid's is also used in CardRefresh
  let isAd
  let isVs

  if (query.programme === 'p-promo-card') {
    return res.redirect('https://player.stv.tv/player-plus?cid=int_stv_em_el_ppluscaroyrly', 301)
  }
  if (query.programme === 'stv-player-vip') {
    return res.redirect('https://click.stv.tv/carouselsportsvip', 301)
  }

  const { STV_GROUPTOKEN } = STV_COOKIES
  let groupToken = cookies.get(STV_GROUPTOKEN, req)
  if (!groupToken || groupToken[2] === EU_REGION_CODE) {
    // we don't have content to show EU users, so just use a default grouptoken
    groupToken = DEFAULT_GROUPTOKEN
  }

  const [
    { results: programmeData, reason: error },
    { data: recommendedData, meta },
  ] = await Promise.all([
    playerApi({
      path: `/programmes/${query.programme}`,
      query: {
        groupToken: DEFAULT_GROUPTOKEN, // programme page shouldn't be restricted by region
      },
    }),
    playerApi({
      path: `/recommended/content/${query.programme}`,
      query: { programmeGuid: query.programme, groupToken, limit: 5 },
    }),
  ])

  const isAccessibleProgramme = ['-vs', '-ad'].includes(programmeData?.guid?.substr(programmeData?.guid?.length - 3))
  if (!isAccessibleProgramme) {
    [
      { results: isAd },
      { results: isVs },
    ] = await Promise.all([
      // check to see if has ad or vs assets available
      playerApi({
        path: `/programmes/${query.programme}-ad`,
        query: {
          groupToken: DEFAULT_GROUPTOKEN, // programme page shouldn't be restricted by region
        },
      }),
      playerApi({
        path: `/programmes/${query.programme}-vs`,
        query: {
          groupToken: DEFAULT_GROUPTOKEN, // programme page shouldn't be restricted by region
        },
      }),
    ])
  }
  if (programmeData) {
    programmeData.accessibility = {
      audioDescribed: Boolean(isAd?.availableEpisodes),
      visuallySigned: Boolean(isVs?.availableEpisodes),
      adData: {
        series: isAd?.series,
        availableEpisodes: isAd?.availableEpisodes,
      },
      vsData: {
        series: isVs?.series,
        availableEpisodes: isVs?.availableEpisodes,
      },
    }
    reduxStore?.dispatch(setHideSponsor(programmeData.premierSportsExclusive))
  }
  if (error) {
    return {
      error: handleApiError({ res, apiError: error }),
    }
  }
  const stvLiveData = await playerApi({
    dispatch: reduxStore?.dispatch,
    path: `/streams/stv`,
    query: { groupToken: DEFAULT_GROUPTOKEN },
  })

  // get autoplay trailer
  const { results } = await playerApi({
    path: `/shortform`,
    query: { groupToken, programme_guid: query.programme },
  })

  if (results) {
    // in future will be multiple as different aspect ratio for mobile
    const autoplayShortform = results?.filter(({ collection }) => collection.guid.includes('autoplay'))[0]

    const autoplayShortformFiltered = {
      videoId: autoplayShortform?.video?.id,
      drmEnabled: autoplayShortform?.programme?.drmEnabled,
      guid: autoplayShortform?.guid,
      videoLength: autoplayShortform?.video?.length,
    }
    // eslint-disable-next-line no-param-reassign
    programmeData.autoplayShortform = autoplayShortformFiltered
  }

  const programmeHeader = extractProgrammeHeaderData(programmeData)
  const nextEpisode = extractNextEpisodeData(programmeData)
  const [featuredEpisode, featuredShortform, puffs] = await Promise.all([
    getFeaturedEpisode({ programmeData, playerApi, req }),
    getFeaturedShortform({
      programmeGuid: query.programme,
      playerApi,
    }),
    getPuffs({
      programmeGuid: query.programme,
    }),
  ])

  const tabs = await getTabs({
    programmeData,
    programmeGuid: query.programme,
    playerApi,
  })
  const recommendedCards = recommendedData?.length
    && recommendedData.map(extractRecommendedData)

  const recommendedDataArray = Array.isArray(recommendedCards) ? recommendedCards : []
  const randomizedRecommendedData = recommendedDataArray
    .map(card => [card, Math.random()])
    .sort((a, b) => a[1] - b[1])
  const recommended = randomizedRecommendedData.slice(0, 5).map(pair => pair[0])

  // Added unique numeral ID of 1, 2, 3, 4, and 5 to recommended object
  // so it can be assigned to a card position
  recommended?.forEach((card, i) => {
    // eslint-disable-next-line no-param-reassign
    card.id = i + 1
  })

  const newsGuids = ['stv-news-glasgow', 'stv-news-edinburgh', 'stv-news-dundee', 'stv-news-aberdeen', 'stv-news-national']
  let newsShortform = null
  if (newsGuids.includes(query.programme?.toLowerCase())) {
    const newsShort = await fetch('https://news.stv.tv/wp-json/newsatsix/v1/latest', {
      method: 'GET',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(r => r.json())

    newsShortform = newsShort.map(r => extractNewsData(r))
  }

  const title = error ? 'Programme not available | STV Player' : `STV Player | ${programmeData.name}`
  const description = programmeData.shortDescription
  const searchTitle = programmeData.name
  const image = getImageRenditions(programmeData?.images?.find(poster => poster.imageType === 'titleImage')?._filepath, ['1024x576'])?._renditions?.['1024x576']
  const hostname = req?.headers?.host?.split(':')[0] || window.location.hostname
  const currentDomain = getCurrentDomain(hostname)
  const { accessibility } = programmeData
  const data = {
    programmeGuid: query.programme,
    programmeHeader,
    nextEpisode,
    featuredEpisode,
    featuredShortform,
    puffs,
    tabs,
    programmeData,
    recommended,
    searchTitle,
    currentDomain,
    newsShortform,
    stvLiveData: stvLiveData?.results,
    accessibility,
    hostname,
  }

  return {
    showWallpaper: true,
    adConfig: {
      path: 'summary',
      programme: query.programme,
    },
    title,
    description,
    data,
    meta,
    image,
    currentDomain,
    layout: 'home',
  }
}

export default withPlayerApi(SummaryContainer)
